import React, { FC, useEffect, useState } from 'react';
import { Button, Form, Input, Popover } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { IconBackground, Spin } from 'common/components';
import { EColor, EFormFieldMessage } from 'common/const';
import { checkForFormErrors, debounce, phoneFormatter, priceFormatter, rules, showSuccessMessage } from 'common/helpers';
import { CHARACTER_LIMIT_MIN } from 'common/config';
import { UserHooks } from 'common/hooks';
import { IAccountShort, IFormValue, ISubdivisionShort } from 'common/models';
import { Affiliate, ArrowDownShort, ArrowUpShort } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { calculateUserBalance, getUserName } from 'entities/User/User.helper';
import { IChangeUserPasswordByCodePayload, ISendChangeUserPasswordCodePayload } from 'entities/User/User.models';
import { ChangePasswordCard, ChangeContactCard, ProfileSettings } from 'entities/User/components';
import {
  changeUserEmail,
  changeUserPasswordByEmailCode,
  changeUserPasswordByPhoneCode,
  changeUserPhone,
  confirmUserEmail,
  confirmUserPhone,
  getCurrentUser,
  selectCurrentUser,
  selectIsUserLoading,
  selectUserError,
  sendChangeUserPasswordCodeByEmail,
  sendChangeUserPasswordCodeByPhone,
  setUserError,
  updateUser,
  updateUserByAdmin,
} from 'entities/User/User.slice';

export const ProfilePage: FC = () => {
  const dispatch = useAppDispatch();
  // user
  const currentUser = useAppSelector(selectCurrentUser);
  const isUserLoading = useAppSelector(selectIsUserLoading);
  const userError = useAppSelector(selectUserError);

  const [openChangePasswordCard, setOpenChangePasswordCard] = useState<boolean>(false);
  const [openChangeEmailCard, setOpenChangeEmailCard] = useState<boolean>(false);
  const [openChangePhoneCard, setOpenChangePhoneCard] = useState<boolean>(false);
  const [openSubdivisionList, setOpenSubdivisionList] = useState<boolean>(false);
  const [form] = useForm();
  const { isAccountAdmin } = UserHooks.useCurrentUserRole();

  const balance = calculateUserBalance(currentUser);

  const handleUpdateUser = (value: IFormValue) => {
    const formHasErrors = checkForFormErrors(form);

    if (currentUser && !formHasErrors) {
      if ('position' in value) {
        dispatch(updateUserByAdmin({ id: currentUser.id, ...value }));
      } else {
        dispatch(updateUser({ id: currentUser.id, ...value }));
      }
    }
  };

  const onSendCode = async (channel: 'email' | 'phone', payload: ISendChangeUserPasswordCodePayload): Promise<void> => {
    dispatch(setUserError(null));

    if (channel === 'email') {
      await dispatch(sendChangeUserPasswordCodeByEmail(payload));
    } else {
      await dispatch(sendChangeUserPasswordCodeByPhone(payload));
    }
  };

  const onPasswordChange = (channel: 'email' | 'phone', value: IChangeUserPasswordByCodePayload) => {
    const { onSuccess, ...rest } = value;
    dispatch(setUserError(null));
    const changePassword = channel === 'email' ? changeUserPasswordByEmailCode : changeUserPasswordByPhoneCode;
    dispatch(
      changePassword({
        ...rest,
        onSuccess: () => {
          onSuccess?.();
          showSuccessMessage('Пароль для входа успешно изменен.');
        },
      }),
    );
  };

  const onChangeEmail = async (email: string, onSuccess: () => void): Promise<void> => {
    dispatch(setUserError(null));
    await dispatch(changeUserEmail({ email, onSuccess }));
  };

  const onConfirmEmail = (email: string, code: string, onSuccess: () => void) => {
    dispatch(setUserError(null));
    dispatch(
      confirmUserEmail({
        email,
        code,
        onSuccess: () => {
          onSuccess();
          setOpenChangeEmailCard(false);
          showSuccessMessage('Электронная почта успешно изменена.');

          if (currentUser) {
            dispatch(getCurrentUser(currentUser.id));
          }
        },
      }),
    );
  };

  const onChangePhone = async (phone: string, onSuccess: () => void): Promise<void> => {
    dispatch(setUserError(null));
    await dispatch(changeUserPhone({ phone, onSuccess }));
  };

  const onConfirmPhone = (phone: string, code: string, onSuccess: () => void) => {
    dispatch(setUserError(null));
    dispatch(
      confirmUserPhone({
        phone,
        code,
        onSuccess: () => {
          onSuccess();
          setOpenChangePhoneCard(false);
          showSuccessMessage('Номер телефона успешно изменен.');

          if (currentUser) {
            dispatch(getCurrentUser(currentUser.id));
          }
        },
      }),
    );
  };

  const renderSubdivision = (subdivision: ISubdivisionShort, account: IAccountShort) => {
    return (
      <div className="flex align-start gap-4">
        <IconBackground content={<Affiliate className="icon-affiliate-dark-grey" />} color={EColor.LightGrey} />

        <div>
          <div className="mb-2 ff-regular fs-s">{subdivision.name}</div>

          <span className="ff-regular fs-xs color-dark-grey">{account.name}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (currentUser) {
      form.setFieldsValue({
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        patronymic: currentUser.patronymic,
        position: currentUser.position,
        email: currentUser.email,
        phone: phoneFormatter(currentUser.phone),
      });
    }
  }, [currentUser]);

  return (
    <Spin spinning={isUserLoading}>
      <div className="redesign h-full flex align-start">
        <div className="h-full flex-1 scrollbar-none profile__content">
          <div className="profile__block">
            <div className="mb-6 ff-regular fs-m color-dark-grey">Личный кабинет</div>

            <div className="mb-11 ff-regular fs-xxl">
              {getUserName.fullName(currentUser?.lastName, currentUser?.firstName, currentUser?.patronymic)}
            </div>

            <div className="flex align-start justify-between">
              {!!currentUser?.subdivisions?.length && (
                <div className="mb-11 flex align-start gap-11">
                  {renderSubdivision(currentUser.subdivisions[0], currentUser.account)}

                  {currentUser.subdivisions.length > 1 && (
                    <Popover
                      overlayClassName="redesign profile__subdivision-list"
                      trigger="click"
                      placement="bottom"
                      open={openSubdivisionList}
                      onOpenChange={setOpenSubdivisionList}
                      content={
                        <div className="overflow-y-scroll scrollbar" style={{ maxHeight: 320 }}>
                          {currentUser.subdivisions.map((item) => {
                            return (
                              <div key={item.id} className="mb-5">
                                {renderSubdivision(item, currentUser.account)}
                              </div>
                            );
                          })}
                        </div>
                      }
                    >
                      <div
                        className={`flex align-center gap-4 cursor-pointer profile__subdivision-list-trigger ${openSubdivisionList ? 'opened' : ''}`}
                      >
                        <span className="ff-semibold fs-xs">{`Все подразделения (${currentUser.subdivisions.length})`}</span>

                        {openSubdivisionList ? (
                          <ArrowUpShort className="icon-arrow-up-short-bright-green" />
                        ) : (
                          <ArrowDownShort className="icon-arrow-up-short-dark-green" />
                        )}
                      </div>
                    </Popover>
                  )}
                </div>
              )}

              {currentUser?.limit && (
                <div className="mb-11 flex align-start gap-11">
                  <div>
                    <div className="mb-2 ff-medium fs-s">{priceFormatter(currentUser?.limit)}</div>

                    <span className="ff-regular fs-xs color-dark-grey">Персональный лимит</span>
                  </div>

                  {balance && (
                    <div>
                      <div className="mb-2 ff-medium fs-s">{priceFormatter(balance)}</div>

                      <span className="ff-regular fs-xs color-dark-grey">Остаток за период</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Form layout="vertical" form={form} onValuesChange={debounce(handleUpdateUser)} requiredMark={false}>
              <div className="mb-11">
                <div className="mb-6 ff-regular fs-l">Персональные данные</div>

                <div className="grid align-end" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  <Form.Item
                    name="lastName"
                    label="Фамилия"
                    rules={[rules.required(), rules.min(CHARACTER_LIMIT_MIN, EFormFieldMessage.CharacterLimitMin)]}
                  >
                    <Input placeholder="Введите фамилию" />
                  </Form.Item>

                  <Form.Item
                    name="firstName"
                    label="Имя"
                    rules={[rules.required(), rules.min(CHARACTER_LIMIT_MIN, EFormFieldMessage.CharacterLimitMin)]}
                  >
                    <Input placeholder="Введите имя" />
                  </Form.Item>

                  <Form.Item name="patronymic" label="Отчество">
                    <Input placeholder="Введите отчество" />
                  </Form.Item>
                </div>

                <Form.Item name="position" label="Должность">
                  <Input placeholder="Укажите вашу должность" disabled={!isAccountAdmin} />
                </Form.Item>
              </div>

              <div className="mb-11">
                <div className="mb-6 ff-regular fs-l">Контактные данные</div>

                <div className="grid align-end" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                  <Form.Item name="email" label="Электронная почта">
                    <Input placeholder="Введите адрес электронной почты" disabled />
                  </Form.Item>

                  <Form.Item name="phone" label="Телефон">
                    <Input placeholder="Введите контактный телефон" disabled />
                  </Form.Item>
                </div>

                <div className="flex gap-4">
                  <Button className="button-sm secondary" onClick={() => setOpenChangeEmailCard(true)}>
                    Изменить почту
                  </Button>

                  <Button className="button-sm secondary" onClick={() => setOpenChangePhoneCard(true)}>
                    Изменить телефон
                  </Button>
                </div>
              </div>
            </Form>

            <div className="mb-11">
              <div className="mb-6 ff-regular fs-l">Пароль</div>

              <Button className="button-sm primary" onClick={() => setOpenChangePasswordCard(true)}>
                Изменить пароль
              </Button>
            </div>
          </div>
        </div>

        <ProfileSettings />

        <ChangePasswordCard
          open={openChangePasswordCard}
          error={userError}
          onClose={() => setOpenChangePasswordCard(false)}
          onSendCode={onSendCode}
          onConfirm={onPasswordChange}
        />

        <ChangeContactCard
          open={openChangeEmailCard}
          type="email"
          error={userError}
          loading={isUserLoading}
          onClose={() => {
            setOpenChangeEmailCard(false);
            dispatch(setUserError(null));
          }}
          onSendCode={onChangeEmail}
          onConfirm={onConfirmEmail}
        />

        <ChangeContactCard
          open={openChangePhoneCard}
          type="phone"
          error={userError}
          loading={isUserLoading}
          onClose={() => {
            setOpenChangePhoneCard(false);
            dispatch(setUserError(null));
          }}
          onSendCode={onChangePhone}
          onConfirm={onConfirmPhone}
        />
      </div>
    </Spin>
  );
};
